import { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import toast from "react-hot-toast";
import Cookies from "js-cookie";
import { LocationModal } from "./locationModel";
import Payment from "../logIn/payment";
import { TextField } from "../common/inputFiled";
import { appStorage } from "../../utils/appStorage";

export const StatusPage = () => {
    const AUDIENCE_RULE_OPTIONS = [
        { id: "family", label: "Family", tags: ["Family"] },
        { id: "friend", label: "Friend", tags: ["Friend"] },
        { id: "work", label: "Work", tags: ["Work"] },
        { id: "other", label: "Other", tags: ["Other"] },
        { id: "family-friend", label: "Family + Friend", tags: ["Family", "Friend"] },
        { id: "family-work", label: "Family + Work", tags: ["Family", "Work"] },
        { id: "friend-work", label: "Friend + Work", tags: ["Friend", "Work"] },
        { id: "family-friend-work", label: "Family + Friend + Work", tags: ["Family", "Friend", "Work"] },
    ];

    const audienceRuleOptionById = AUDIENCE_RULE_OPTIONS.reduce((acc, option) => {
        acc[option.id] = option;
        return acc;
    }, {});

    const audienceRuleOrder = ["Family", "Friend", "Work"];

    const parseAudienceTags = (value = "") => {
        const rawValues = Array.isArray(value)
            ? value.flatMap((item) => String(item).split(/(?:,|\+)/))
            : String(value).split(/(?:,|\+)/);

        return Array.from(
            new Set(
                rawValues
                    .map((item) => item.trim())
                    .filter(Boolean)
            )
        );
    };

    const canonicalAudienceRule = (value = "") => {
        const tags = parseAudienceTags(String(value).replace(/["']/g, ""));

        if (!tags.length) return "Other";

        if (tags.length === 1 && tags[0].toLowerCase() === "other") {
            return "Other";
        }

        const normalizedTags = Array.from(
            new Set(
                tags
                    .map((tag) => tag.trim())
                    .filter((tag) => tag && tag.toLowerCase() !== "other")
                    .map((tag) => tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase())
            )
        );

        const orderedTags = audienceRuleOrder.filter((tag) => normalizedTags.includes(tag));

        if (!orderedTags.length) return "Other";

        return orderedTags.join(" + ");
    };

    const parseAudienceRuleLabels = (value = "") => {
        const values = Array.isArray(value)
            ? value
            : String(value || "").split(",");

        return Array.from(
            new Set(
                values
                    .map((item) => canonicalAudienceRule(item))
                    .filter(Boolean)
            )
        );
    };

    const normalizeAudienceRuleSelection = (value = "") => {
        const labels = parseAudienceRuleLabels(value);

        return labels
            .sort((left, right) => {
                const leftIndex = AUDIENCE_RULE_OPTIONS.findIndex((option) => option.label === left);
                const rightIndex = AUDIENCE_RULE_OPTIONS.findIndex((option) => option.label === right);
                return leftIndex - rightIndex;
            })
            .join(", ");
    };

    const getAudienceRuleSelection = (ruleLabel = "Other") => {
        const ruleLabels = parseAudienceRuleLabels(ruleLabel);

        return AUDIENCE_RULE_OPTIONS.filter((option) => ruleLabels.includes(option.label)).map((option) => option.id);
    };

    const serializeAudienceRuleSelection = (optionIds = []) => {
        const selectedLabels = optionIds
            .map((optionId) => audienceRuleOptionById[optionId]?.label)
            .filter(Boolean);

        if (!selectedLabels.length) return "Other";

        return selectedLabels
            .sort((left, right) => {
                const leftIndex = AUDIENCE_RULE_OPTIONS.findIndex((option) => option.label === left);
                const rightIndex = AUDIENCE_RULE_OPTIONS.findIndex((option) => option.label === right);
                return leftIndex - rightIndex;
            })
            .join(", ");
    };

    const findAudienceRuleConflict = (ruleLabel, currentStatusId = null) => {
        const selectedRules = parseAudienceRuleLabels(ruleLabel).map((label) => label.toLowerCase());

        return statuses.find((status) => {
            if (currentStatusId && String(status.status_id) === String(currentStatusId)) return false;
            const existingRules = parseAudienceRuleLabels(status.show_relation).map((label) => label.toLowerCase());
            return selectedRules.some((label) => existingRules.includes(label));
        });
    };

    const getUsedAudienceRules = (currentStatusId = null) => {
        return new Set(
            statuses.flatMap((status) => {
                if (currentStatusId && String(status.status_id) === String(currentStatusId)) {
                    return [];
                }

                return parseAudienceRuleLabels(status.show_relation).map((label) => label.toLowerCase());
            })
        );
    };

    const isAudienceRuleDisabled = (optionId) => {
        const option = audienceRuleOptionById[optionId];
        if (!option) return false;

        const usedRules = getUsedAudienceRules(selectedId);
        return usedRules.has(option.label.toLowerCase());
    };

    const findStatusFromDuplicateMessage = (message = "") => {
        const match = String(message).match(/show_relation\s+"([^"]+)"/i);
        if (!match) return null;

        const relation = normalizeAudienceRuleSelection(match[1]);
        return statuses.find(
            (status) => normalizeAudienceRuleSelection(status.show_relation) === relation
        );
    };

    const validateStatusForm = () => {
        const trimmedText = statusText.trim();

        if (enableUntil && selectedAudienceOptions.length === 0) {
            toast.dismiss();
            toast.error("Select at least one audience tag.");
            return false;
        }

        if (!trimmedText && !enableUntil) {
            toast.dismiss();
            toast.error("Enter a status message.");
            return false;
        }

        return true;
    };

    const audienceTagButtonClass = (isSelected, isDisabled) =>
        `flex h-11 w-full items-center justify-center rounded-full border-2 px-3 text-center text-xs font-medium leading-tight transition-all duration-200 sm:text-sm ${
            isSelected
                ? "border-[#6938EF] bg-[#6938EF] text-white"
                : isDisabled
                    ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400 opacity-70"
                    : "border-gray-300 bg-white text-gray-700 hover:border-[#6938EF]"
        }`;

    const getStatusAudienceRule = () =>
        enableUntil ? serializeAudienceRuleSelection(selectedAudienceOptions) : "Other";

    const [addStatus, setAddStatus] = useState(false);
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [statuses, setStatuses] = useState([]);
    const [selectedAudienceOptions, setSelectedAudienceOptions] = useState([]);
    const [statusText, setStatusText] = useState("");
    const [statusDate, setStatusDate] = useState("");
    const [statusTime, setStatusTime] = useState("");
    const [enableUntil, setEnableUntil] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const timeInputRef = useRef(null);

    const canEnablePaidFeature = () => {
        const isPaid = paymentStatus === 1;
        if (isPaid) return true;
        // Open payment modal instead of showing a toast
        setPaymentModalOpen(true);
        return false;
    };

    const token = Cookies.get("auth_token");

    const fetchProfile = useCallback(async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/profile`, {
                method: "GET",
                headers: { Authorization: `Bearer ${token}` },
            });

            const data = await res.json();
            if (data?.status === "1") {
                const apiPaymentStatus = data?.data?.[0]?.profile?.payment_status;
                if (typeof apiPaymentStatus !== "undefined") {
                    setPaymentStatus(Number(apiPaymentStatus));
                    appStorage.setItem("payment_status", String(apiPaymentStatus));
                }
            }
        } catch (err) {
            }
    }, [token]);

    // Fetch simple status list
    const fetchStatuses = useCallback(async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/status/get`, {
                method: "GET",
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();

            if (data.status === "1" && Array.isArray(data.data)) {
                setStatuses(data.data);
            } else {
                setStatuses([]);
            }
        } catch (err) {
            toast.dismiss();
            toast.error(err.message || "Failed to fetch statuses");
        }
    }, [token]);

    useEffect(() => {
        fetchProfile();
        fetchStatuses();
    }, [fetchProfile, fetchStatuses]);

    // Convert 12-hour format (HH:MM AM/PM) to 24-hour format (HH:MM:00)
    const timeTo24 = (timeStr = "") => {
        if (!timeStr) return "00:00:00";
        // Handle 24-hour format directly (from time input)
        if (timeStr.match(/^\d{2}:\d{2}$/)) {
            return `${timeStr}:00`;
        }
        // Handle 12-hour format (HH:MM AM/PM)
        const m = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
        if (!m) return "00:00:00";
        let hh = parseInt(m[1], 10);
        const mm = m[2];
        const ampm = (m[3] || "").toUpperCase();
        if (ampm === "PM" && hh < 12) hh += 12;
        if (ampm === "AM" && hh === 12) hh = 0;
        return `${String(hh).padStart(2, "0")}:${mm}:00`;
    };

    // Convert UTC date and time back to local time
    const convertUTCToLocal = (dateStr, timeStr) => {
        if (!dateStr || !timeStr) return { date: dateStr, time: timeStr };

        try {
            // Parse UTC time string (HH:MM:SS format)
            const [hours, minutes, seconds] = timeStr.split(":").map(Number);

            // Create a UTC date object
            const utcDate = new Date();
            utcDate.setUTCFullYear(parseInt(dateStr.split("-")[0]), parseInt(dateStr.split("-")[1]) - 1, parseInt(dateStr.split("-")[2]));
            utcDate.setUTCHours(hours, minutes, seconds || 0, 0);

            // Convert to local time
            const localYear = utcDate.getFullYear();
            const localMonth = String(utcDate.getMonth() + 1).padStart(2, "0");
            const localDay = String(utcDate.getDate()).padStart(2, "0");
            const localHours = String(utcDate.getHours()).padStart(2, "0");
            const localMinutes = String(utcDate.getMinutes()).padStart(2, "0");
            const localSeconds = String(utcDate.getSeconds()).padStart(2, "0");

            return {
                date: `${localYear}-${localMonth}-${localDay}`,
                time: `${localHours}:${localMinutes}:${localSeconds}`
            };
        } catch (err) {
            return { date: dateStr, time: timeStr };
        }
    };

    // Convert local date and time to UTC
    const convertToUTC = (dateStr, timeStr) => {
        if (!dateStr || !timeStr) return { date: dateStr, time: timeTo24(timeStr) };

        // Parse the date and time
        const time24 = timeTo24(timeStr); // HH:MM:SS format
        const [hours, minutes, seconds] = time24.split(":").map(Number);

        // Create a local date object with the selected date and time
        const localDate = new Date(dateStr);
        localDate.setHours(hours, minutes, seconds || 0, 0);

        // Get UTC values directly from the Date object
        const utcYear = localDate.getUTCFullYear();
        const utcMonth = String(localDate.getUTCMonth() + 1).padStart(2, "0");
        const utcDay = String(localDate.getUTCDate()).padStart(2, "0");
        const utcHours = String(localDate.getUTCHours()).padStart(2, "0");
        const utcMinutes = String(localDate.getUTCMinutes()).padStart(2, "0");
        const utcSeconds = String(localDate.getUTCSeconds()).padStart(2, "0");

        return {
            date: `${utcYear}-${utcMonth}-${utcDay}`,
            time: `${utcHours}:${utcMinutes}:${utcSeconds}`
        };
    };

    // Convert 24-hour format (HH:MM:00 or HH:MM) to 24-hour format for time input (HH:MM)
    const time24ToInput = (timeStr = "") => {
        if (!timeStr) return "";
        // Extract HH:MM from HH:MM:00 or HH:MM
        const match = timeStr.match(/(\d{2}):(\d{2})/);
        if (match) {
            return `${match[1]}:${match[2]}`;
        }
        return "";
    };

    const getTodayInputDate = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, "0");
        const day = String(now.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const getCurrentInputTime = () => {
        const now = new Date();
        const hours = String(now.getHours()).padStart(2, "0");
        const minutes = String(now.getMinutes()).padStart(2, "0");
        return `${hours}:${minutes}`;
    };

    const todayInputDate = getTodayInputDate();
    const minStatusTime = statusDate === todayInputDate ? getCurrentInputTime() : undefined;

    const handleStatusDateChange = (value) => {
        const nextDate = value && value < todayInputDate ? todayInputDate : value;
        setStatusDate(nextDate);

        if (nextDate === todayInputDate && statusTime && statusTime < getCurrentInputTime()) {
            setStatusTime(getCurrentInputTime());
        }
    };

    const handleStatusTimeChange = (value) => {
        if (statusDate === todayInputDate && value && value < getCurrentInputTime()) {
            setStatusTime(getCurrentInputTime());
            return;
        }

        setStatusTime(value);
    };

    const buildStatusPayload = (normalizedRule, statusId = null) => {
        const payload = {
            text: statusText || "",
            show_relation: normalizedRule,
        };

        if (statusId) {
            payload.status_id = statusId;
        }

        if (enableUntil && statusDate) {
            const utcData = convertToUTC(statusDate, statusTime);
            payload.date = utcData.date;
            payload.time = utcData.time;
        }

        return payload;
    };

    // CREATE STATUS
    const handleSetStatus = async () => {
        try {
            if (!validateStatusForm()) return;

            const normalizedRule = getStatusAudienceRule();
            const existingStatus = findAudienceRuleConflict(normalizedRule);

            if (existingStatus) {
                toast.dismiss();
                toast.error("One or more selected options are already used in another status.");
                return;
            }

            const payload = buildStatusPayload(normalizedRule);

            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/status/create`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            const data = await res.json();
            if (data.status === "1") {
                toast.success("Status set successfully");
                fetchStatuses();
                resetForm();
            } else {
                const duplicateStatus =
                    findStatusFromDuplicateMessage(data.message) ||
                    findAudienceRuleConflict(normalizedRule);

                if (duplicateStatus) {
                    toast.dismiss();
                    toast.error(data.message || "One or more selected options are already used in another status.");
                    return;
                }

                toast.dismiss();
                toast.error(data.message || "Failed to set status");
            }
        } catch (err) {
            toast.dismiss();
            toast.error(err.message || "Network error");
        }
    };

    // UPDATE STATUS
    const handleUpdateStatus = async () => {
        try {
            if (!validateStatusForm()) return;

            const normalizedRule = getStatusAudienceRule();

            if (enableUntil && findAudienceRuleConflict(normalizedRule, selectedId)) {
                toast.dismiss();
                toast.error(`A status for "${normalizedRule}" already exists.`);
                return;
            }

            const payload = buildStatusPayload(normalizedRule, selectedId);

            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/status/update`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            const data = await res.json();

            if (data.status === "1") {
                toast.success("Status updated successfully");
                fetchStatuses();
                resetForm();
            } else {
                toast.dismiss();
                toast.error(data.message || "Failed to update status");
            }
        } catch (err) {
            toast.dismiss();
            toast.error(err.message || "Network error");
        }
    };

    const isOfflineUntilStatus = (status = {}) =>
        Number(status.offlineuntil) === 1 ||
        Boolean(status.date && status.time) ||
        Boolean(status.until);

    const resetForm = () => {
        setAddStatus(false);
        setSelectedId(null);
        setStatusText("");
        setStatusDate("");
        setStatusTime("");
        setEnableUntil(false);
        setSelectedAudienceOptions([]);
    };

    const handleCardClick = (status) => {
        setAddStatus(true);

        setSelectedId(status.status_id);

        // Text
        setStatusText(status.text || "");

        setSelectedAudienceOptions(getAudienceRuleSelection(status.show_relation));

        if (isOfflineUntilStatus(status)) {
            setEnableUntil(true);

            if (status.date && status.time) {
                const localData = convertUTCToLocal(status.date, status.time);
                setStatusDate(localData.date);
                setStatusTime(time24ToInput(localData.time));
            } else if (status.until) {
                const d = new Date(status.until);
                setStatusDate(d.toISOString().split("T")[0]);
                const hours = String(d.getHours()).padStart(2, "0");
                const minutes = String(d.getMinutes()).padStart(2, "0");
                setStatusTime(`${hours}:${minutes}`);
            } else {
                setStatusDate("");
                setStatusTime("");
            }
        } else {
            setEnableUntil(false);
            setStatusDate("");
            setStatusTime("");
        }
    };

    const toggleAudienceRule = (optionId) => {
        setSelectedAudienceOptions((prev) => {
            if (prev.includes(optionId)) {
                return prev.filter((id) => id !== optionId);
            }

            if (isAudienceRuleDisabled(optionId)) {
                toast.dismiss();
                toast.error("This option is already used in another status.");
                return prev;
            }

            return [...prev, optionId];
        });
    };

    const [deleteModal, setDeleteModal] = useState({ open: false, statusId: null });
    const handleDelete = (e, statusId) => {
        e.stopPropagation();
        setDeleteModal({ open: true, statusId });
    };

    const confirmDelete = async () => {
        const statusId = deleteModal.statusId;
        setDeleteModal({ open: false, statusId: null });
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/status/delete`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    Authorization: `Bearer ${token}`,
                },
                body: new URLSearchParams({ status_id: statusId }).toString(),
            });
            const data = await res.json();
            if (data.status === "1") {
                toast.success("Status deleted successfully!");
                fetchStatuses();
            } else {
                toast.error(data.message || "Failed to delete status");
            }
        } catch (err) {
            toast.error(err.message || "Failed to delete status");
        }
    };

    const isAudienceOptionSelected = (optionId) => selectedAudienceOptions.includes(optionId);
    const isAudienceOptionUnavailable = (optionId) =>
        !isAudienceOptionSelected(optionId) && isAudienceRuleDisabled(optionId);

    // ...existing code...
    return (
        <>
            {/* Delete Confirmation Modal */}
            {deleteModal.open && ReactDOM.createPortal(
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
                    <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-sm text-center">
                        <div className="mb-4 flex justify-center flex-col items-center">
                            <div className="h-14 w-14 flex mb-4 justify-center items-center bg-[linear-gradient(180deg,#8862F2_0%,#7544FC_29%,#40248B_100%)]
 rounded-full !p-0 shadow-sm transition-opacity"
                            >
                                <svg fill="#ffffff" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className="w-10 h-10">
                                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-bold text-[#6938EF] mb-2">Delete Status?</h3>
                            <p className="text-gray-600">Are you sure you want to delete this status? This action cannot be undone.</p>
                        </div>
                        <div className="flex gap-4 justify-center mt-6">
                            <button
                                className="px-6 py-2 rounded-full bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition"
                                onClick={() => setDeleteModal({ open: false, statusId: null })}
                            >
                                Cancel
                            </button>
                            <button
                                className="px-6 py-2 rounded-full submit-button font-semibold"
                                onClick={confirmDelete}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
            {addStatus ? (
                <div>
                    <div className="rounded-2xl p-4 sm:p-5 md:p-6 border border-gray-100 shadow-sm overflow-hidden">
                        <div className="flex justify-between items-center gap-3">
                            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                                <button
                                    onClick={() => resetForm()}
                                    className="p-2 rounded-full transition shrink-0"
                                    aria-label="Go back"
                                >
                                    <img src="./back-arrow.svg" alt="back-arrow" className="h-5 w-5" />

                                </button>
                                <h2 className="truncate text-lg font-semibold text-gray-900">
                                    {selectedId ? "Edit Status" : "Add Status"}
                                </h2>
                            </div>
                            <div className="relative group shrink-0">
                                <button
                                    type="button"
                                    className="h-9 w-9 flex items-center justify-center rounded-full bg-[#F3F0FF] text-[#6938EF] border border-[#E0D7FF] hover:bg-[#EAE3FF] transition"
                                    aria-label="Status tag info"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                        className="w-5 h-5"
                                    >
                                        <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm.75 15h-1.5v-6h1.5v6zM12 8.5a.9.9 0 110-1.8.9.9 0 010 1.8z" />
                                    </svg>
                                </button>

                                <div className="absolute right-0 mt-3 w-[calc(100vw-2rem)] max-w-sm sm:w-96 rounded-2xl bg-white border border-gray-200 shadow-xl p-4 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition z-10">


                                    <ul className="text-xs text-gray-700 space-y-1.5">
                                        <li>• Only Work tags</li>
                                        <li>• Only Friend tags</li>
                                        <li>• Only Family tags</li>
                                        <li>• Friend + Family tags (contacts with multiple tags)</li>
                                        <li>• Friend + Work tags (contacts with multiple tags)</li>
                                        <li>• Work + Family tags (contacts with multiple tags)</li>
                                        <li>• Friend + Family + Work tags (contacts with multiple tags)</li>
                                        <li>• Separate option to select Others</li>
                                    </ul>

                                    <div className="mt-3 rounded-xl border border-[#EAE3FF] bg-[#F8F6FF] p-3 sm:p-4 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
                                        {/* Venn diagram — Family / Friends / Work */}
                                        <div className="relative w-44 h-44 flex-shrink-0">
                                            {/* Family — top centre */}
                                            <button
                                                type="button"
                                                onClick={() => toggleAudienceRule("family")}
                                                aria-pressed={isAudienceOptionSelected("family")}
                                                disabled={isAudienceOptionUnavailable("family")}
                                                className={`absolute top-0 left-1/2 -translate-x-1/2 w-24 h-24 rounded-full flex items-start justify-center pt-4 transition-transform duration-150 ${isAudienceOptionSelected("family") ? "bg-[#7A59F0] scale-105 z-30 shadow-lg text-white" : isAudienceOptionUnavailable("family") ? "bg-gray-200 text-gray-400 cursor-not-allowed opacity-60" : "bg-[#B5ADEE]/70 text-gray-800"}`}
                                            >
                                                <span className="text-[12px] font-bold">Family</span>
                                            </button>

                                            {/* Friend — bottom left */}
                                            <button
                                                type="button"
                                                onClick={() => toggleAudienceRule("friend")}
                                                aria-pressed={isAudienceOptionSelected("friend")}
                                                disabled={isAudienceOptionUnavailable("friend")}
                                                className={`absolute bottom-6 left-0 w-24 h-24 rounded-full flex items-end justify-start pl-7 pb-4 transition-transform duration-150 ${isAudienceOptionSelected("friend") ? "bg-[#3EA36A] scale-105 z-30 shadow-lg text-white" : isAudienceOptionUnavailable("friend") ? "bg-gray-200 text-gray-400 cursor-not-allowed opacity-60" : "bg-[#A8D5B5]/70 text-gray-800"}`}
                                            >
                                                <span className="text-[12px] font-bold">Friend</span>
                                            </button>

                                            {/* Work — bottom right */}
                                            <button
                                                type="button"
                                                onClick={() => toggleAudienceRule("work")}
                                                aria-pressed={isAudienceOptionSelected("work")}
                                                disabled={isAudienceOptionUnavailable("work")}
                                                className={`absolute bottom-6 right-0 w-24 h-24 rounded-full flex items-end justify-end pr-7 pb-4 transition-transform duration-150 ${isAudienceOptionSelected("work") ? "bg-[#D4B43C] scale-105 z-30 shadow-lg text-white" : isAudienceOptionUnavailable("work") ? "bg-gray-200 text-gray-400 cursor-not-allowed opacity-60" : "bg-[#F0E08A]/70 text-gray-800"}`}
                                            >
                                                <span className="text-[12px] font-bold">Work</span>
                                            </button>
                                        </div>

                                        {/* Others — standalone circle */}
                                        <button
                                            type="button"
                                            onClick={() => toggleAudienceRule("other")}
                                            aria-pressed={isAudienceOptionSelected("other")}
                                            disabled={isAudienceOptionUnavailable("other")}
                                            className={`w-24 h-24 rounded-full flex items-center justify-center flex-shrink-0 transition-transform duration-150 ${isAudienceOptionSelected("other") ? "bg-[#C9B8F5] scale-105 shadow-lg" : isAudienceOptionUnavailable("other") ? "bg-gray-200 cursor-not-allowed opacity-60" : "bg-[#C9B8F5]/70"}`}
                                        >
                                            <span className={`text-[12px] font-bold ${isAudienceOptionUnavailable("other") ? "text-gray-400" : "text-gray-800"}`}>Others</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="pb-6 md:h-[84dvh] md:overflow-y-auto [&::-webkit-scrollbar]:hidden">
                            <div className="mt-4 gap-4">

                                {/* ENABLE UNTIL SWITCH */}
                                <label className="flex items-center gap-2 cursor-pointer mt-3">
                                    <span className="text-sm text-gray-500">Offline until</span>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={enableUntil}
                                            onChange={(e) => {
                                                const { checked } = e.target;
                                                if (!checked) {
                                                    setSelectedAudienceOptions([]);
                                                    setStatusDate("");
                                                    setStatusTime("");
                                                    setEnableUntil(false);
                                                    return;
                                                }
                                                if (canEnablePaidFeature()) {
                                                    setEnableUntil(true);
                                                }
                                            }}
                                        />
                                        <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-[#6938EF] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:h-4 after:w-4 after:rounded-full after:transition-all peer-checked:after:translate-x-[16px]" />
                                    </label>
                                </label>

                                {/* DATE + TIME */}
                                {enableUntil && (
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <TextField
                                            label="Date"
                                            type="date"
                                            name="date"
                                            value={statusDate}
                                            minDate={todayInputDate}
                                            startYear={new Date().getFullYear()}
                                            endYear={new Date().getFullYear() + 10}
                                            onChange={(e) => handleStatusDateChange(e.target.value)}
                                            startIcon="../input-logos/calander.svg"
                                        />

                                        <div className="space-y-1">
                                            <label className="block text-xs font-medium input-lable my-3">
                                                Time
                                            </label>
                                            <div className="relative">
                                                <img
                                                    src="../input-logos/clock.svg"
                                                    alt=""
                                                    className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2"
                                                />
                                                <input
                                                    ref={timeInputRef}
                                                    type="time"
                                                    name="time"
                                                    value={statusTime}
                                                    min={minStatusTime}
                                                    onChange={(e) => handleStatusTimeChange(e.target.value)}
                                                    onClick={() => timeInputRef.current?.showPicker?.()}
                                                    className="w-full h-11 rounded-[13px] border border-[#E6E2FF] bg-white py-3 pl-10 pr-10 text-gray-800 outline-none shadow-[inset_0_0_0_1px_rgba(108,69,255,0.15)] transition focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {enableUntil && (
                                <div className="my-6 space-y-5">
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 mb-3 block">Single Tags</label>
                                        <div className="grid grid-cols-2 min-[360px]:grid-cols-4 gap-2 sm:gap-3">
                                            {AUDIENCE_RULE_OPTIONS.filter((option) => option.tags.length === 1).map((option) => {
                                                const isSelected = isAudienceOptionSelected(option.id);
                                                const isDisabled = isAudienceOptionUnavailable(option.id);
                                                return (
                                                    <button
                                                        key={option.id}
                                                        onClick={() => toggleAudienceRule(option.id)}
                                                        type="button"
                                                        disabled={isDisabled}
                                                        title={isDisabled ? "Already used in another status" : undefined}
                                                        className={audienceTagButtonClass(isSelected, isDisabled)}
                                                    >
                                                        {option.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 mb-3 block">Dual Tags</label>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-3">
                                            {AUDIENCE_RULE_OPTIONS.filter((option) => option.tags.length === 2).map((option) => {
                                                const isSelected = isAudienceOptionSelected(option.id);
                                                const isDisabled = isAudienceOptionUnavailable(option.id);
                                                return (
                                                    <button
                                                        key={option.id}
                                                        onClick={() => toggleAudienceRule(option.id)}
                                                        type="button"
                                                        disabled={isDisabled}
                                                        title={isDisabled ? "Already used in another status" : undefined}
                                                        className={audienceTagButtonClass(isSelected, isDisabled)}
                                                    >
                                                        {option.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 mb-3 block">Triple Tag</label>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                                            {AUDIENCE_RULE_OPTIONS.filter((option) => option.tags.length === 3).map((option) => {
                                                const isSelected = isAudienceOptionSelected(option.id);
                                                const isDisabled = isAudienceOptionUnavailable(option.id);
                                                return (
                                                    <button
                                                        key={option.id}
                                                        onClick={() => toggleAudienceRule(option.id)}
                                                        type="button"
                                                        disabled={isDisabled}
                                                        title={isDisabled ? "Already used in another status" : undefined}
                                                        className={audienceTagButtonClass(isSelected, isDisabled)}
                                                    >
                                                        {option.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                                )}

                                {/* TEXT */}  {!enableUntil && (
                                <>
                                <label className="text-sm font-semibold text-gray-700 mb-3 block">Status</label>
                                <textarea
                                    value={statusText}
                                    onChange={(e) => setStatusText(e.target.value)}
                                    onFocus={(e) => {
                                        window.setTimeout(() => {
                                            e.target.scrollIntoView({ block: "center", behavior: "smooth" });
                                        }, 300);
                                    }}
                                    className="w-full h-36 sm:h-64 rounded-[13px] border bg-white px-4 py-3 outline-none transition focus:border-[#6938EF] focus:ring-4 focus:ring-[#6938EF]/10"
                                    placeholder="Enter your status (120 Characters)"
                                />
                                </>
                                )}
                                {/* SUBMIT */}
                                <div className="mt-6 flex justify-center">
                                    <button
                                        type="button"
                                        onClick={selectedId ? handleUpdateStatus : handleSetStatus}
                                        className="submit-button w-full sm:w-96"
                                    >
                                        {selectedId ? "Update" : "Set Status"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <LocationModal
                        show={showLocationModal}
                        onClose={() => setShowLocationModal(false)}
                    />
                </div>
            ) : (
                <div className="rounded-2xl p-5 md:p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-lg font-semibold text-gray-900">Status</h2>
                        <button
                            onClick={() => {
                                setAddStatus(true);
                            }}
                            className="p-2 hover:bg-gray-100 rounded-full bg-white shadow-md"
                        >
                            <img src="./add.svg" alt="Add" className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="space-y-6 h-[70dvh] overflow-y-auto py-5">
                        {statuses.length === 0 ? (
                            <div className="flex flex-col h-[70dvh] justify-center items-center">
                                <img src="./no-data/no-status.svg" alt="No data" className="mx-auto mb-4 h-40 w-40" />
                                <p className="font-semibold text-[28px]">No status added yet.</p>
                                <p className="text-gray-500 text-center">This user hasn’t added status yet.</p>
                            </div>) : (
                            statuses.map((s) => (
                                <div
                                    key={s.status_id}
                                    onClick={() => handleCardClick(s)}
                                    className="relative overflow-hidden rounded-3xl border border-[#E9E4FF] bg-white p-5  transition duration-200 cursor-pointer group hover:-translate-y-0.5"
                                >
                                    <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#7C4DFF] via-[#6B47F5] to-[#44C2FF]" />
                                    <button
                                        onClick={(e) => handleDelete(e, s.status_id)}
                                        className="absolute top-4 right-4 h-8 w-8 flex justify-center items-center rounded-full  bg-[#6B47F5] hover:bg-[#5A3EE3] shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                                        title="Delete"
                                    >
                                        <svg fill="#ffffff" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 sm:w-4 sm:h-4">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                    </svg>
                                    </button>
                                    <div className="flex items-start gap-4 pr-10">
                                        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-[#F3F0FF] text-[#6B47F5] shadow-sm">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6">
                                                <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm.75 15h-1.5v-6h1.5v6zM12 8.5a.9.9 0 110-1.8.9.9 0 010 1.8z" />
                                            </svg>
                                        </div>

                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#6B47F5]">Status</span>
                                                <span className="rounded-full bg-[#F3F0FF] px-3 py-1 text-xs font-medium text-[#5B4DBA]">
                                                    {isOfflineUntilStatus(s) ? "Offline until scheduled time" : "No expiry set"}
                                                </span>
                                            </div>

                                            {s.text ? (
                                                <p className="mt-3 text-[15px] leading-6 font-medium text-gray-900 break-words">
                                                    {s.text}
                                                </p>
                                            ) : (
                                                <p className="mt-3 text-sm text-gray-500 italic">
                                                    No status message added.
                                                </p>
                                            )}

                                            {s.date && s.time && (
                                                <p className="mt-3 text-xs font-medium text-gray-500">
                                                    Expires on {s.date} at {s.time}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
            {paymentModalOpen && (
                <Payment open={paymentModalOpen} onClose={() => setPaymentModalOpen(false)} />
            )}
        </>
    );
};
